import { Component } from '../helpers/helper';
import { state } from '../state';

const TILE_ASPECT = 16 / 9;
const GAP = 8;
const PADDING = 8;

// Compute the optimal grid layout (cols x rows) that maximizes per-tile area
// for `n` 16:9 tiles inside a container of `width` x `height`.
function computeLayout(n: number, width: number, height: number) {
	if (n <= 0 || width <= 0 || height <= 0) {
		return { cols: 1, rows: 1, tileW: 0, tileH: 0 };
	}
	const innerW = Math.max(0, width - PADDING * 2);
	const innerH = Math.max(0, height - PADDING * 2);
	let best = { cols: 1, rows: n, tileW: 0, tileH: 0, area: -1 };
	for (let cols = 1; cols <= n; cols++) {
		const rows = Math.ceil(n / cols);
		const cellW = (innerW - GAP * (cols - 1)) / cols;
		const cellH = (innerH - GAP * (rows - 1)) / rows;
		if (cellW <= 0 || cellH <= 0) continue;
		// Fit a 16:9 tile inside the cell.
		let tileW = cellW;
		let tileH = tileW / TILE_ASPECT;
		if (tileH > cellH) {
			tileH = cellH;
			tileW = tileH * TILE_ASPECT;
		}
		const area = tileW * tileH;
		if (area > best.area) {
			best = { cols, rows, tileW, tileH, area };
		}
	}
	return best;
}

export const VideoGrid = Component(() => {
	const containerRef = Vue.ref<HTMLElement | null>(null);
	const size = Vue.reactive({ w: 0, h: 0 });
	let ro: ResizeObserver | null = null;

	function measure() {
		const el = containerRef.value;
		if (!el) return;
		size.w = el.clientWidth;
		size.h = el.clientHeight;
	}

	Vue.onMounted(() => {
		measure();
		if (typeof ResizeObserver !== 'undefined' && containerRef.value) {
			ro = new ResizeObserver(() => measure());
			ro.observe(containerRef.value);
		}
		window.addEventListener('resize', measure);
	});

	Vue.onBeforeUnmount(() => {
		if (ro) ro.disconnect();
		window.removeEventListener('resize', measure);
	});

	const layout = Vue.computed(() => computeLayout(state.users.length, size.w, size.h));

	// Recompute when the user count changes (DOM may not have laid out yet).
	Vue.watch(() => state.users.length, () => Vue.nextTick(measure));

	return () => {
		const l = layout.value;
		const tileStyle = {
			width: l.tileW > 0 ? `${l.tileW}px` : 'auto',
			height: l.tileH > 0 ? `${l.tileH}px` : 'auto'
		};
		const gridStyle = {
			gridTemplateColumns: `repeat(${l.cols}, ${l.tileW > 0 ? l.tileW + 'px' : '1fr'})`,
			gridAutoRows: l.tileH > 0 ? `${l.tileH}px` : 'auto',
			gap: `${GAP}px`,
			padding: `${PADDING}px`,
			justifyContent: 'center',
			alignContent: 'center'
		};
		return (
			<div id="video-grid" ref={containerRef} style={gridStyle} class={{ 'gallery-responsive': true }}>
				{state.users.map(u => (
					<div
						key={u.id}
						class={{ 'video-tile': true, pinned: state.pinnedUserId === u.id }}
						style={tileStyle}
						onClick={() => state.pinUser(u.id)}
					>
						{u.stream ? (
							<video
								srcObject={u.stream}
								autoplay
								muted={u.me}
								class={{ flip: u.me && state.sharingCam }}
							/>
						) : (
							<div
								class="tile-avatar"
								style={{ backgroundColor: `${u.color}22` }}
							>
								{u.name ? u.name[0].toUpperCase() : '?'}
							</div>
						)}
						<div class="tile-overlay">
							<span class="tile-name">{u.name}</span>
							{u.handRaised && <span class="tile-hand-raise">✋</span>}
							{u.connectionQuality && (
								<span class={`tile-quality quality-indicator ${u.connectionQuality}`}>●</span>
							)}
						</div>
						{u.reactions.length > 0 && (
							<div class="tile-reactions">
								{u.reactions.map(r => (
									<span key={r.id} class="floating-reaction">{r.emoji}</span>
								))}
							</div>
						)}
					</div>
				))}
			</div>
		);
	};
});

